// WHAT: Component for displaying a single quadrant in the Impact vs Effort Matrix
// WHY:  Provides visual representation of items categorized by impact and effort
// DOMAIN: Admin → Roadmap → Matrix
// DEPENDENCIES: Card, Badge components, Roadmap_Item type, Lucide icons
// RISKS:
//   - NOTE: Dynamic Tailwind classes may not work with JIT compiler
//   - NOTE: Hover scale effects may cause layout shifts
//   - NOTE: Status color mapping assumes specific status values

import {
  LucideCheckCircle,
  LucideClock,
  LucideFolderOpen,
  LucideAlertTriangle,
  LucideBarChart3,
} from "lucide-react";
import React from "react";

import { Card, Badge } from "@/components/ui";

import { type Roadmap_Item } from "../../../types/roadmap-types";

/**
 * WHAT:
 *   Returns configuration (color, icon) for a given status.
 *
 * WHY:
 *   Provides visual consistency for status representation in item cards.
 *
 * BEHAVIOR:
 *   - Maps known statuses to specific colors and icons
 *   - Returns default configuration for unknown statuses
 *
 * @param status - The status string to get configuration for
 *
 * @returns {object} Configuration object with color and icon
 *
 */
const getStatusConfig = (status: string) => {
  switch (status) {
    case "done": {
      return { color: "green-500", icon: LucideCheckCircle };
    }
    case "in_progress": {
      return { color: "orange-500", icon: LucideClock };
    }
    case "open": {
      return { color: "blue-500", icon: LucideFolderOpen };
    }
    case "rejected": {
      return { color: "red-500", icon: LucideAlertTriangle };
    }
    default: {
      return { color: "slate-500", icon: LucideBarChart3 };
    }
  }
};

/**
 * Props for MatrixQuadrant component
 */
interface MatrixQuadrantProperties {
  /** Background color class for quadrant */
  color: string;
  /** Quadrant description (e.g., "High Impact, Low Effort") */
  description: string;
  /** Icon component for quadrant */
  icon: React.ComponentType<{ className?: string }>;
  /** Unique identifier for the quadrant */
  id: string;
  /** Array of roadmap items in this quadrant */
  items: Roadmap_Item[];
  /** Priority number (1-4) */
  priority: number;
  /** Text color class for quadrant */
  textColor: string;
  /** Quadrant title (e.g., "Quick Wins") */
  title: string;
}

/**
 * WHAT:
 *   Component that displays a single quadrant of the Impact vs Effort Matrix
 *   with roadmap items categorized by impact and effort scores.
 *
 * WHY:
 *   Provides visual organization of roadmap items based on prioritization criteria,
 *   helping users identify quick wins, major projects, and items to avoid.
 *
 * PROPS:
 *   - id: string — Unique quadrant identifier
 *   - title: string — Quadrant title
 *   - description: string — Quadrant description
 *   - color: string — Background color class
 *   - textColor: string — Text color class
 *   - icon: React.ComponentType — Icon component
 *   - priority: number — Priority number (1-4)
 *   - items: Roadmap_Item[] — Items in this quadrant
 *
 * BEHAVIOR:
 *   - Displays quadrant header with color indicator, title, description, and icon
 *   - Renders item cards with status icons, title, description, module, and priority badges
 *   - Shows empty state when no items
 *   - Displays item count and priority badge at bottom
 *   - Applies hover scale effects for interactivity
 *
 * RISKS:
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly - use static classes
 *   - NOTE: Hover scale effects may cause layout shifts on smaller screens
 *   - NOTE: Status color mapping assumes specific status values - unknown statuses get default
 *
 * @param root0
 *
 * @param root0.title
 *
 * @param root0.description
 *
 * @param root0.color
 *
 * @param root0.textColor
 *
 * @param root0.icon
 *
 * @param root0.priority
 *
 * @param root0.items
 *
 * @returns {JSX.Element} Die gerenderte MatrixQuadrant-Komponente
 *
 */
export const MatrixQuadrant: React.FC<MatrixQuadrantProperties> = ({
  color,
  description,
  icon: Icon,
  items,
  priority,
  textColor,
  title,
}) => {
  return (
    <Card
      className="group hover:scale-[1.01] transition-all duration-200"
      padding="lg"
      variant="default"
    >
      {/* Quadrant Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-4 h-4 rounded-full shadow-lg bg-${color}`} />
        <div className="flex-1">
          <h3 className={`text-lg font-semibold mb-1 text-${textColor}`}>{title}</h3>
          <p className={`text-sm text-${textColor}/80`}>{description}</p>
        </div>
        <div
          className={`p-2 rounded-lg transition-all duration-200 group-hover:scale-110 bg-${color}/20`}
        >
          <Icon className={`w-5 h-5 text-${textColor}`} />
        </div>
      </div>

      {/* Items List */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-center py-8">
            <div
              className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center bg-${color}/20`}
            >
              <Icon className={`w-6 h-6 text-${textColor}`} />
            </div>
            <p className="text-slate-500 text-sm italic">No items in this category</p>
          </div>
        ) : (
          items.map((item) => {
            const statusConfig = getStatusConfig(item.status);
            const StatusIcon = statusConfig.icon;

            return (
              <Card
                className="group/item hover:scale-[1.02] transition-all duration-200"
                key={item.id}
                padding="md"
                variant="outlined"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <StatusIcon className={`w-4 h-4 text-${statusConfig.color}`} />
                      <h4 className="text-white font-medium text-sm truncate">{item.title}</h4>
                    </div>
                    <p className="text-slate-400 text-xs line-clamp-2 mb-3">{item.description}</p>
                    <div className="flex items-center gap-2">
                      <Badge size="sm" style="subtle" variant="default">
                        {item.module}
                      </Badge>
                      {item.priority === "high" && (
                        <Badge size="sm" style="subtle" variant="destructive">
                          High Priority
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Item Count */}
      <div className="mt-6 pt-4 border-t border-slate-600">
        <div className="flex items-center justify-between">
          <p className="text-slate-400 text-sm font-medium">
            {items.length} {items.length === 1 ? "Item" : "Items"}
          </p>
          <Badge size="sm" style="subtle" variant="default">
            Priority {priority}
          </Badge>
        </div>
      </div>
    </Card>
  );
};
